import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'

import {
  createFileRoute,
  Link,
  notFound,
  Outlet,
  useMatches,
  useNavigate,
} from '@tanstack/react-router'

import {
  getAccountDetailsAppAccountsAccountsAccountIdGetOptions,
  getAccountHistoryAppAccountsHistoryAccountIdGetOptions,
  getAccountProcessingStatusAppAccountsProcessingStatusAccountIdGetOptions,
  trackAccountViewAppAccountsAccountsAccountIdViewPostMutation,
} from '@/client/@tanstack/react-query.gen'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAnalytics } from '@/hooks/use-analytics'
import { useAutoTour } from '@/hooks/use-tour'
import { Suspense, useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Loader2, XCircle } from 'lucide-react'
import { AccountDetailSkeleton } from './-components/skeleton'
import { AccountDetailsHeader } from './-components/account-details-header'
import { AccountMetaRow } from './-components/account-meta-row'

export const Route = createFileRoute('/_authed/_user/accounts/$accountId')({
  component: AccountView,
  validateSearch: () => ({}),
  params: {
    parse: ({ accountId }) => ({ accountId: Number(accountId) }),
    stringify: ({ accountId }) => ({ accountId: String(accountId) }),
  },
  beforeLoad: async ({ params, context }) => {
    try {
      const data = await context.queryClient.ensureQueryData(
        getAccountDetailsAppAccountsAccountsAccountIdGetOptions({
          path: { account_id: params.accountId },
        }),
      )

      const companyName = data.company?.companyName || data.account?.accountName || 'Account'
      return {
        getTitle: () => companyName,
      }
    }
    catch (error) {
      if (error instanceof Error && 'status' in error && (error as { status: number }).status === 404) {
        throw notFound()
      }
      throw error
    }
  },
  loader: async ({ params }: { params: { accountId: number } }) => {
    return { accountId: params.accountId }
  },
  notFoundComponent: AccountNotFoundFallback,
  errorComponent: AccountNotFoundFallback,
})

function AccountNotFoundFallback() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold">404</h1>
        <h2 className="text-2xl font-semibold">Account Not Found</h2>
        <p className="text-muted-foreground">
          The account you&apos;re looking for doesn&apos;t exist or you
          don&apos;t have access to it.
        </p>
        <Button asChild>
          <Link to="/accounts">Back to Accounts</Link>
        </Button>
      </div>
    </div>
  )
}
function AISearchingAnimation() {
  return (
    <div className="relative w-32 h-32">
      {/* Floating particles */}
      <div className="absolute inset-0">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 rounded-full bg-primary/30"
            style={{
              left: `${20 + (i * 12)}%`,
              top: `${15 + (i % 3) * 30}%`,
              animation: `float ${2 + (i * 0.3)}s ease-in-out infinite`,
              animationDelay: `${i * 0.2}s`,
            }}
          />
        ))}
      </div>

      {/* Bouncing logo */}
      <div className="absolute inset-0 flex items-center justify-center animate-bounce-gentle">
        <svg
          width="64"
          height="64"
          viewBox="0 0 157 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="text-primary"
        >
          <path
            d="M88.1459 0.135841L132.125 0.135841C133.145 0.135841 134.166 0.73724 134.677 1.59638L155.773 38.5395C156.283 39.4846 156.283 40.6015 155.773 41.5466L134.677 78.4897C134.166 79.4348 133.145 79.9503 132.125 79.9503L93.2498 79.9503C90.953 79.9503 89.5069 77.4587 90.6978 75.4827L99.7999 59.5026C100.565 58.2139 102.182 57.6984 103.543 58.2998C106.265 59.4167 109.242 59.9322 112.39 59.7604C121.236 59.159 128.807 52.3717 130.253 43.5226C131.699 34.6734 127.531 26.7693 120.726 22.9031C117.834 21.1849 114.516 20.2398 110.943 20.2398C102.522 20.2398 96.21 25.8353 92.5693 33.1269L69.3465 78.4897C68.8361 79.4348 67.8153 79.9503 66.7945 79.9503H24.517C23.4963 79.9503 22.4755 79.3489 21.9651 78.4897L0.868878 41.5466C0.358486 40.6015 0.358486 39.4846 0.868878 38.5395L21.9651 1.59638C22.4755 0.651326 23.4963 0.135841 24.517 0.135841L63.3919 0.135841C65.6887 0.135841 67.1348 2.62736 65.9438 4.60339L56.8419 20.4975C56.0763 21.7863 54.5451 22.3017 53.099 21.7003C50.8022 20.7553 48.2503 20.2398 45.6132 20.2398C34.8099 20.2398 26.0482 29.089 26.0482 40.086C26.0482 47.3887 29.9612 53.8323 35.8307 57.183C38.7229 58.9012 42.0405 59.8463 45.6132 59.8463C54.0347 59.8463 60.4349 53.9881 63.9873 46.8732L88.0608 0.0499268L88.1459 0.135841Z"
            fill="currentColor"
          />
        </svg>
      </div>

      {/* Orbiting magnifying glass */}
      <div
        className="absolute w-full h-full"
        style={{ animation: 'orbit 3s linear infinite' }}
      >
        <div className="absolute -right-2 top-1/2 -translate-y-1/2">
          <svg
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-primary/70"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
        </div>
      </div>

      {/* CSS for animations */}
      <style>
        {`
          @keyframes orbit {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0) scale(1); opacity: 0.3; }
            50% { transform: translateY(-10px) scale(1.2); opacity: 0.6; }
          }
          .animate-bounce-gentle {
            animation: bounce-gentle 2s ease-in-out infinite;
          }
          @keyframes bounce-gentle {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-8px); }
          }
        `}
      </style>
    </div>
  )
}

function AccountProcessingState({
  status,
  workflowId,
}: {
  status: 'pending' | 'failed'
  workflowId?: string
}) {
  if (status === 'pending') {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-6">
        <AISearchingAnimation />
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-semibold">Our AI is on the case!</h2>
          <p className="text-muted-foreground max-w-md">
            We&apos;re gathering company data, finding contacts, and generating insights.
            Hang tight!
          </p>
          {workflowId && (
            <p className="text-xs text-muted-foreground mt-4">
              Taking too long? Contact support with workflow ID:
              {' '}
              {workflowId}
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-[60vh] flex-col items-center justify-center gap-6">
      <XCircle className="h-16 w-16 text-destructive" />
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-semibold">Processing Failed</h2>
        <p className="text-muted-foreground max-w-md">
          Something went wrong while processing this account.
          Please contact support for assistance.
        </p>
        {workflowId && (
          <p className="text-xs text-muted-foreground mt-4">
            Workflow ID:
            {' '}
            {workflowId}
          </p>
        )}
      </div>
      <Button asChild>
        <Link to="/accounts">Back to Accounts</Link>
      </Button>
    </div>
  )
}

function formatCount(count: number): string {
  if (count === 0)
    return ''
  if (count > 99)
    return '99+'
  if (count > 20)
    return `${Math.floor(count / 10) * 10}+`
  return `${count}`
}

function AccountView() {
  useAutoTour('account-detail-intro')
  const { accountId } = Route.useLoaderData()
  const { data: accountDetails } = useSuspenseQuery(
    getAccountDetailsAppAccountsAccountsAccountIdGetOptions({
      path: { account_id: accountId },
    }),
  )

  // Check processing status to determine if account is still being enriched
  const { data: processingStatus } = useQuery({
    ...getAccountProcessingStatusAppAccountsProcessingStatusAccountIdGetOptions({
      path: { account_id: accountId },
    }),
  })

  const { data: historyData } = useQuery(
    getAccountHistoryAppAccountsHistoryAccountIdGetOptions({
      path: { account_id: accountId },
    }),
  )

  const navigate = useNavigate()
  const matches = useMatches()
  const { capture } = useAnalytics()

  // Track account view
  const { mutate: trackView } = useMutation(trackAccountViewAppAccountsAccountsAccountIdViewPostMutation())
  useEffect(() => {
    trackView({ path: { account_id: accountId } })
  }, [accountId, trackView])

  // Get current tab from route - extract last segment of deepest match
  const currentMatch = matches.at(-1)
  const currentTab = currentMatch?.pathname.split('/').findLast(Boolean) || 'overview'

  useEffect(() => {
    capture('account_detail_tab_viewed', {
      account_id: accountId,
      tab: currentTab,
      source: 'direct_url',
    })
  }, [accountId, capture, currentTab])

  // Compute history event count for the tab badge
  const historyCount = (historyData?.companyEvents?.length ?? 0)
    + (historyData?.accountActions?.length ?? 0)

  const historyBadge = formatCount(historyCount)
  const contactsBadge = formatCount(accountDetails.contactsCount ?? 0)

  const tabs = [
    { value: 'overview', label: 'Overview', mobileLabel: 'Overview', badge: '' },
    { value: 'history', label: 'Account History', mobileLabel: 'History', badge: historyBadge },
    { value: 'contacts', label: 'Relevant Contacts', mobileLabel: 'Contacts', badge: contactsBadge },
    { value: 'locations', label: 'Locations', mobileLabel: 'Locations', badge: '' },
  ] as const

  // Check account processing status - the 'account' entity represents the full enrichment workflow
  const accountEntity = processingStatus?.entities?.find(
    entity => entity.entityType === 'account',
  )

  const isPending = accountEntity && (
    accountEntity.status === 'NOT_STARTED'
    || accountEntity.status === 'QUEUED'
    || accountEntity.status === 'RUNNING'
  )

  // Check if we have meaningful enriched data to display (not just the basic account shell)
  const hasCompanyData = !!(
    accountDetails.firmographics
    || accountDetails.finance
    || accountDetails.misc
    || accountDetails.account.leadQualification?.triageScore
    || accountDetails.competitors?.competitors?.length
    || accountDetails.projects?.offerings?.length
  )

  // Show full processing state only if we have NO data to display
  if (isPending && !hasCompanyData) {
    return (
      <AccountProcessingState
        status="pending"
        workflowId={accountEntity.workflowId}
      />
    )
  }

  // Note: We no longer block the entire view when processing fails or is pending.
  // If we have company data, we show it with a processing indicator.
  // The AccountOpportunities component handles showing partial/error states for triage.

  return (
    <ErrorBoundary FallbackComponent={AccountNotFoundFallback}>
      <Suspense
        fallback={(
          <div className="flex h-full w-full items-center justify-center py-10">
            <AccountDetailSkeleton />
          </div>
        )}
      >
        <div className="flex min-h-0 flex-1 flex-col">
          {isPending && hasCompanyData && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 px-3 py-2 rounded-md mb-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Enrichment in progress - some data may still be loading...</span>
            </div>
          )}
          {accountDetails.company !== undefined && (
            <div className="flex-none pt-4 md:pt-6 pb-4 px-4 lg:px-6">
              <AccountDetailsHeader
                accountId={accountId}
                companyName={accountDetails.company.companyName || ''}
                logoUrl={accountDetails.logoUrl || ''}
                websiteUrl={accountDetails.company.websiteUrl || undefined}
                websiteDomain={accountDetails.company.websiteDomain || undefined}
                websiteScrapeStatus={accountDetails.company.websiteScrapeStatus}
                linkedinUrl={accountDetails.firmographics?.linkedinUrl || undefined}
                accountDetails={accountDetails}
              />
            </div>
          )}
          <div className="flex flex-1 flex-col min-h-0">
            <Tabs
              value={currentTab}
              className="flex flex-1 flex-col min-h-0"
              onValueChange={(value) => {
                navigate({ to: `/accounts/${accountId}/${value}` })
              }}
            >
              <TabsList data-tour="account-detail-tabs" className="relative z-20 flex h-10 min-h-10 w-full flex-none justify-start overflow-x-auto rounded-none border-b bg-background p-0 px-4 lg:px-6">
                {tabs.map(({ value, label, mobileLabel, badge }) => (
                  <TabsTrigger
                    key={value}
                    value={value}
                    className="relative z-20 h-full shrink-0 rounded-none border-b-2 border-transparent px-2.5 py-2 text-sm md:px-4 md:text-base data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                  >
                    <span className="md:hidden">{mobileLabel}</span>
                    <span className="hidden md:inline">{label}</span>
                    {badge && (
                      <span className="ml-1.5 inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                        {badge}
                      </span>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
              <AccountMetaRow accountDetails={accountDetails} />

              <Outlet />
            </Tabs>
          </div>
        </div>
      </Suspense>
    </ErrorBoundary>
  )
}
